import { Camera, Pin, Ruler, WifiOff } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { VADA_FAZE_VZNIKU } from '@helfr/domain';
import { api, hlaskaZChyby } from '../api/client.js';
import type { Ukol, Vada, VykresDetail, VykresPins, VykresVerze } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { jeOnline } from '../lib/offline.js';
import { nactiPdf, type PdfDokument } from '../lib/pdf.js';
import { formatDen } from '../lib/format.js';
import {
  UKOL_DALSI_STAV,
  UKOL_STAV_BARVA,
  UKOL_STAV_LABEL,
  VADA_DALSI_STAV,
  VADA_FAZE_LABEL,
  VADA_STAV_BARVA,
  VADA_STAV_LABEL,
} from '../lib/labels.js';
import { Badge, Chyba, Input, Nacitani, Select, Textarea, VelkeTlacitko } from './ui.js';

/**
 * Viewer výkresu sdílený terénem i kanceláří (Blok C):
 *  - PDF na canvasu (pdfjs, lib/pdf.ts), pinch-zoom + pan (pointer events),
 *    zoom = CSS transformace — žádné překreslování při gestu;
 *  - přepínač verzí; při NEplatné verzi trvalý výrazný banner s přepnutím
 *    („stavělo se podle starého výkresu" = jádro bolesti, invariant 6);
 *  - piny úkolů (modré) a vad (červené), hotové šedé; tap → detail
 *    v bottom sheetu; „Připnout" → tap do výkresu → miniformulář →
 *    POST /api/tasks|defects s pinX/pinY NORMALIZOVANĚ 0–1 (jak ukládá
 *    backend, lib/pin.ts) na KONKRÉTNÍ zobrazenou verzi;
 *  - DWG jen ke stažení (žádný rendering, f2);
 *  - offline: výkresy se nepředstahují (velké soubory, f2) — jen čitelný
 *    prázdný stav.
 */

const soubUrl = (vykresId: string, verzeId: string): string =>
  `/api/drawings/${vykresId}/versions/${verzeId}/soubor`;

/** Zaokrouhlení normalizované souřadnice (0–1) na 4 desetinná místa. */
const norm = (n: number): number => Math.min(1, Math.max(0, Math.round(n * 10000) / 10000));

interface Transformace {
  s: number;
  tx: number;
  ty: number;
}

type PdfStav =
  | { stav: 'nacitam' }
  | { stav: 'hotovo' }
  | { stav: 'offline' }
  | { stav: 'chyba'; hlaska: string };

export function VykresViewer({ vykres }: { vykres: VykresDetail }) {
  const { maPravo } = useAuth();
  const smiPripnout = maPravo('denik.write') || maPravo('projekty.write');

  const [verzeId, setVerzeId] = useState<string>(
    vykres.platnaVerze?.id ?? vykres.verze[0]?.id ?? '',
  );
  const verze = vykres.verze.find((v) => v.id === verzeId);
  const platna = vykres.platnaVerze ?? vykres.verze.find((v) => v.jePlatna) ?? null;
  const jePdf = (verze?.mimeType ?? '') === 'application/pdf';

  // Piny na zobrazené verzi (pin patří VERZI, ne výkresu — invariant 6).
  const { data: pins } = useQuery({
    queryKey: ['vykres-pins', verzeId],
    queryFn: () => api.get<VykresPins>(`/api/drawings/versions/${verzeId}/pins`),
    enabled: !!verzeId,
  });

  const [rezimPripnuti, setRezimPripnuti] = useState(false);
  const [novyPin, setNovyPin] = useState<{ x: number; y: number } | null>(null);
  const [otevrenyPin, setOtevrenyPin] = useState<{ typ: 'ukol' | 'vada'; id: string } | null>(null);

  const prepniVerzi = (id: string) => {
    setVerzeId(id);
    setRezimPripnuti(false);
    setNovyPin(null);
    setOtevrenyPin(null);
  };

  if (!verze) {
    return <p className="p-4 text-text-secondary">Výkres zatím nemá žádnou verzi.</p>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {/* Hlavička vieweru: verze + připnutí. */}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex min-w-0 flex-1 items-center gap-2">
          <span className="shrink-0 text-base font-semibold">Verze</span>
          <Select
            aria-label="Verze výkresu"
            value={verzeId}
            onChange={(e) => prepniVerzi(e.target.value)}
          >
            {vykres.verze.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.verze} — {formatDen(v.datum ?? v.createdAt)}
                {v.jePlatna ? ' (platná)' : ''}
              </option>
            ))}
          </Select>
        </label>
        {jePdf && smiPripnout && !rezimPripnuti && !novyPin && (
          <button
            type="button"
            className="inline-flex min-h-12 items-center gap-2 rounded-lg border-2 border-primary bg-surface px-4 text-base font-bold text-primary transition-colors duration-100 hover:bg-primary-tint"
            onClick={() => setRezimPripnuti(true)}
          >
            <Pin size={18} strokeWidth={2} aria-hidden="true" /> Připnout
          </button>
        )}
      </div>

      {/* TRVALÝ banner neplatné verze — jádro bolesti „stavělo se podle starého výkresu". */}
      {!verze.jePlatna && (
        <div
          role="alert"
          className="flex flex-wrap items-center justify-between gap-3 rounded-lg border-2 border-error bg-error-bg p-3 text-error-text"
        >
          <p className="text-base font-bold">
            ⚠ Toto NENÍ platná verze
            {platna
              ? ` (platná: v${platna.verze} z ${formatDen(platna.datum ?? platna.createdAt)})`
              : ''}
            .
          </p>
          {platna && (
            <button
              type="button"
              className="min-h-12 rounded-md bg-error-text px-4 text-base font-bold text-white transition duration-100 hover:brightness-110"
              onClick={() => prepniVerzi(platna.id)}
            >
              Přepnout na platnou
            </button>
          )}
        </div>
      )}

      {rezimPripnuti && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-lg border-2 border-info-border bg-info-bg p-3"
        >
          <p className="text-base font-semibold text-info-text">
            Ťukněte do výkresu, kam pin patří.
          </p>
          <button
            type="button"
            className="min-h-12 shrink-0 px-2 font-semibold text-info-text underline underline-offset-2"
            onClick={() => setRezimPripnuti(false)}
          >
            Zrušit
          </button>
        </div>
      )}

      {jePdf ? (
        <PdfPlocha
          key={verzeId}
          url={soubUrl(vykres.id, verzeId)}
          pins={pins}
          rezimPripnuti={rezimPripnuti}
          naUmisteniPinu={(bod) => {
            setRezimPripnuti(false);
            setNovyPin(bod);
          }}
          naOtevreniPinu={setOtevrenyPin}
        />
      ) : (
        <DwgKarta vykres={vykres} verze={verze} />
      )}

      {novyPin && (
        <NovyPinSheet
          zakazkaId={vykres.zakazkaId}
          verzeId={verzeId}
          bod={novyPin}
          zavri={() => setNovyPin(null)}
        />
      )}
      {otevrenyPin && (
        <PinDetailSheet
          pin={otevrenyPin}
          smiZapisovat={smiPripnout}
          zavri={() => setOtevrenyPin(null)}
        />
      )}
    </div>
  );
}

/** DWG: žádný rendering (f2) — karta s tlačítkem ke stažení. */
function DwgKarta({ vykres, verze }: { vykres: VykresDetail; verze: VykresVerze }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface p-6 text-center">
      <Ruler size={40} strokeWidth={1.5} aria-hidden="true" className="text-text-secondary" />
      <p className="text-base text-text-primary">
        DWG výkres se v prohlížeči nezobrazuje — stáhněte si soubor a otevřete ho v CAD programu.
      </p>
      <a
        className="inline-flex min-h-14 w-full max-w-xs items-center justify-center rounded-lg bg-primary px-4 text-lg font-bold text-white transition-colors duration-100 hover:bg-primary-hover"
        href={soubUrl(vykres.id, verze.id)}
        download={verze.nazevSouboru ?? undefined}
      >
        ⬇ Stáhnout DWG
      </a>
    </div>
  );
}

/**
 * Plocha PDF: canvas + overlay pinů v jednom transformovaném kontejneru.
 * Gesta: 1 prst = posun, 2 prsty = pinch-zoom, kolečko = zoom (desktop).
 */
function PdfPlocha({
  url,
  pins,
  rezimPripnuti,
  naUmisteniPinu,
  naOtevreniPinu,
}: {
  url: string;
  pins: VykresPins | undefined;
  rezimPripnuti: boolean;
  naUmisteniPinu: (bod: { x: number; y: number }) => void;
  naOtevreniPinu: (pin: { typ: 'ukol' | 'vada'; id: string }) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const plochaRef = useRef<HTMLDivElement>(null);
  // Overlay pinů (transformovaný souřadný systém) — klik se měří proti němu.
  const overlayRef = useRef<HTMLDivElement>(null);
  const [dokument, setDokument] = useState<PdfDokument | null>(null);
  const [pdfStav, setPdfStav] = useState<PdfStav>({ stav: 'nacitam' });
  const [stranka, setStranka] = useState(1);
  const [rozmer, setRozmer] = useState<{ sirka: number; vyska: number } | null>(null);
  const [t, setT] = useState<Transformace>({ s: 1, tx: 0, ty: 0 });

  // Načtení dokumentu (jednou na verzi/URL).
  useEffect(() => {
    let zruseno = false;
    let nacteny: PdfDokument | null = null;
    setPdfStav({ stav: 'nacitam' });
    setDokument(null);
    nactiPdf(url)
      .then((doc) => {
        if (zruseno) {
          doc.znic();
          return;
        }
        nacteny = doc;
        setDokument(doc);
      })
      .catch((err: unknown) => {
        if (zruseno) return;
        // Offline se výkresy nepředstahují (velké soubory, plné předstažení f2).
        setPdfStav(jeOnline() ? { stav: 'chyba', hlaska: hlaskaZChyby(err) } : { stav: 'offline' });
      });
    return () => {
      zruseno = true;
      nacteny?.znic();
    };
  }, [url]);

  // Uživatel už sám zoomoval/posouval → přestaneme automaticky dopočítávat fit.
  const uzInteragoval = useRef(false);

  /** Přizpůsobení šířce plochy (fit-to-width) — resetuje i posun. */
  const fitNaSirku = (r: { sirka: number; vyska: number }) => {
    const sirkaPlochy = plochaRef.current?.clientWidth ?? 0;
    const fit = sirkaPlochy > 0 && r.sirka > 0 ? sirkaPlochy / r.sirka : 1;
    setT({ s: fit, tx: 0, ty: 0 });
  };

  // Vykreslení stránky na canvas + úvodní přizpůsobení šířce.
  useEffect(() => {
    if (!dokument) return;
    let zruseno = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    uzInteragoval.current = false;
    dokument
      .vykresliStranku(stranka, canvas)
      .then((r) => {
        if (zruseno) return;
        setRozmer(r);
        fitNaSirku(r);
        setPdfStav({ stav: 'hotovo' });
      })
      .catch((err: unknown) => {
        if (!zruseno) setPdfStav({ stav: 'chyba', hlaska: hlaskaZChyby(err) });
      });
    return () => {
      zruseno = true;
    };
  }, [dokument, stranka]);

  // Přepočet fitu při změně šířky plochy (kancelář: viewer se rozbalí v tabulce,
  // clientWidth se ustálí až po layoutu → jinak PDF zůstane špatně zmenšené).
  // Dokud uživatel sám nezoomoval, držíme plán přizpůsobený šířce.
  useEffect(() => {
    const el = plochaRef.current;
    if (!el || !rozmer || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      if (!uzInteragoval.current) fitNaSirku(rozmer);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [rozmer]);

  // --- Gesta (pointer events) ---
  const pointery = useRef(new Map<number, { x: number; y: number }>());
  const pohyb = useRef(0);

  const zoomOkoloBodu = (kolik: number, bod: { x: number; y: number }) => {
    uzInteragoval.current = true;
    setT((prev) => {
      const s2 = Math.min(8, Math.max(0.2, prev.s * kolik));
      const k = s2 / prev.s;
      return { s: s2, tx: bod.x - (bod.x - prev.tx) * k, ty: bod.y - (bod.y - prev.ty) * k };
    });
  };

  const naPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointery.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointery.current.size === 1) pohyb.current = 0;
  };

  const naPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const predchozi = pointery.current.get(e.pointerId);
    if (!predchozi) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const nyni = { x: e.clientX, y: e.clientY };
    pohyb.current += Math.abs(nyni.x - predchozi.x) + Math.abs(nyni.y - predchozi.y);

    if (pointery.current.size === 2) {
      // Pinch: poměr vzdáleností + střed gesta zůstává na místě.
      const [a, b] = [...pointery.current.entries()].map(([id, p]) =>
        id === e.pointerId ? { id, p: nyni, stary: p } : { id, p, stary: p },
      );
      if (a && b) {
        const dist = (p1: { x: number; y: number }, p2: { x: number; y: number }) =>
          Math.hypot(p1.x - p2.x, p1.y - p2.y) || 1;
        const k = dist(a.p, b.p) / dist(a.stary, b.stary);
        const stred = {
          x: (a.p.x + b.p.x) / 2 - rect.left,
          y: (a.p.y + b.p.y) / 2 - rect.top,
        };
        zoomOkoloBodu(k, stred);
      }
    } else if (pointery.current.size === 1) {
      uzInteragoval.current = true;
      setT((prev) => ({
        ...prev,
        tx: prev.tx + (nyni.x - predchozi.x),
        ty: prev.ty + (nyni.y - predchozi.y),
      }));
    }
    pointery.current.set(e.pointerId, nyni);
  };

  const naPointerKonec = (e: React.PointerEvent<HTMLDivElement>) => {
    pointery.current.delete(e.pointerId);
  };

  // Kolečko myši (kancelář): zoom kolem kurzoru; nativní listener kvůli
  // preventDefault (React wheel je pasivní).
  useEffect(() => {
    const el = plochaRef.current;
    if (!el) return;
    const naKolecko = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      zoomOkoloBodu(e.deltaY < 0 ? 1.15 : 1 / 1.15, {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };
    el.addEventListener('wheel', naKolecko, { passive: false });
    return () => el.removeEventListener('wheel', naKolecko);
  }, []);

  // Tap do výkresu v režimu připnutí → normalizované souřadnice 0–1
  // (nezávislé na zoomu i rozlišení — přesně jak je ukládá backend).
  // Handler visí na PLOŠE (pointer capture u myši posílá click sem, ne na
  // overlay), souřadnice se ale měří proti transformovanému overlayi.
  const naKlikDoVykresu = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!rezimPripnuti || pohyb.current > 8) return;
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;
    naUmisteniPinu({
      x: norm((e.clientX - rect.left) / (rect.width || 1)),
      y: norm((e.clientY - rect.top) / (rect.height || 1)),
    });
  };

  if (pdfStav.stav === 'offline') {
    return (
      <div className="flex flex-col items-center gap-2 rounded-lg border border-border bg-surface p-8 text-center">
        <WifiOff size={40} strokeWidth={1.5} aria-hidden="true" className="text-text-secondary" />
        <p className="text-lg font-bold">Výkres není dostupný offline</p>
        <p className="text-base text-text-secondary">
          Výkresy jsou velké soubory a zatím se do telefonu nepředstahují. Připojte se k internetu a
          otevřete výkres znovu.
        </p>
      </div>
    );
  }
  if (pdfStav.stav === 'chyba') {
    return <Chyba>Výkres se nepodařilo načíst: {pdfStav.hlaska}</Chyba>;
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      <div
        ref={plochaRef}
        className="relative h-[60vh] w-full touch-none overflow-hidden rounded-lg border border-border-strong bg-fill-disabled"
        onPointerDown={naPointerDown}
        onPointerMove={naPointerMove}
        onPointerUp={naPointerKonec}
        onPointerCancel={naPointerKonec}
        role={rezimPripnuti ? 'button' : undefined}
        aria-label={rezimPripnuti ? 'Umístit pin ťuknutím do výkresu' : undefined}
        onClick={naKlikDoVykresu}
      >
        {pdfStav.stav === 'nacitam' && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <Nacitani text="Načítám výkres…" />
          </div>
        )}
        <div
          className="relative origin-top-left"
          style={{
            width: rozmer?.sirka,
            height: rozmer?.vyska,
            transform: `translate(${t.tx}px, ${t.ty}px) scale(${t.s})`,
          }}
        >
          <canvas ref={canvasRef} className="block bg-surface" />
          {/* Overlay pinů — stejný souřadný systém jako canvas (0–1 × rozměr). */}
          <div ref={overlayRef} data-testid="pin-overlay" className="absolute inset-0">
            {(pins?.ukoly ?? [])
              .filter((u) => u.pinX != null && u.pinY != null)
              .map((u) => (
                <PinBod
                  key={u.id}
                  nazev={`Pin úkolu: ${u.nazev}`}
                  barva={
                    u.stav === 'hotovo' || u.stav === 'zamitnuto' ? 'bg-text-muted' : 'bg-info'
                  }
                  x={u.pinX ?? 0}
                  y={u.pinY ?? 0}
                  meritko={t.s}
                  neaktivni={rezimPripnuti}
                  onClick={() => naOtevreniPinu({ typ: 'ukol', id: u.id })}
                />
              ))}
            {(pins?.vady ?? [])
              .filter((v) => v.pinX != null && v.pinY != null)
              .map((v) => (
                <PinBod
                  key={v.id}
                  nazev={`Pin vady: ${v.popis}`}
                  barva={v.stav === 'prekontrolovano' ? 'bg-text-muted' : 'bg-error'}
                  x={v.pinX ?? 0}
                  y={v.pinY ?? 0}
                  meritko={t.s}
                  neaktivni={rezimPripnuti}
                  onClick={() => naOtevreniPinu({ typ: 'vada', id: v.id })}
                />
              ))}
          </div>
        </div>
      </div>

      {dokument && dokument.pocetStranek > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            className="min-h-12 min-w-12 rounded-md border-2 border-border-strong bg-surface text-lg font-bold transition-colors duration-100 hover:bg-bg disabled:border-border disabled:text-text-disabled"
            disabled={stranka <= 1}
            onClick={() => setStranka((s) => s - 1)}
          >
            ◀
          </button>
          <span className="text-base font-semibold">
            Strana {stranka} / {dokument.pocetStranek}
          </span>
          <button
            type="button"
            className="min-h-12 min-w-12 rounded-md border-2 border-border-strong bg-surface text-lg font-bold transition-colors duration-100 hover:bg-bg disabled:border-border disabled:text-text-disabled"
            disabled={stranka >= dokument.pocetStranek}
            onClick={() => setStranka((s) => s + 1)}
          >
            ▶
          </button>
        </div>
      )}
    </div>
  );
}

/** Bod pinu — 44px dotyková plocha, viditelná tečka; inverzní scale drží velikost. */
function PinBod({
  nazev,
  barva,
  x,
  y,
  meritko,
  neaktivni,
  onClick,
}: {
  nazev: string;
  barva: string;
  x: number;
  y: number;
  meritko: number;
  neaktivni: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-label={nazev}
      className={`absolute flex h-11 w-11 items-center justify-center ${
        neaktivni ? 'pointer-events-none' : ''
      }`}
      style={{
        left: `${x * 100}%`,
        top: `${y * 100}%`,
        transform: `translate(-50%, -50%) scale(${1 / (meritko || 1)})`,
      }}
      // Pointerdown NESMÍ probublat do plochy: ta si bere pointer capture
      // (pan) a u MYŠI by click skončil na ploše místo pinu — na dotyku to
      // fungovalo jen proto, že click se syntetizuje z touch událostí.
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <span
        aria-hidden="true"
        className={`block h-6 w-6 rounded-full border-2 border-white shadow-md ${barva}`}
      />
    </button>
  );
}

/** Bottom sheet s detailem pinu (název, stav, foto) + akce dle práv. */
function PinDetailSheet({
  pin,
  smiZapisovat,
  zavri,
}: {
  pin: { typ: 'ukol' | 'vada'; id: string };
  smiZapisovat: boolean;
  zavri: () => void;
}) {
  const queryClient = useQueryClient();
  const url = pin.typ === 'ukol' ? `/api/tasks/${pin.id}` : `/api/defects/${pin.id}`;
  const { data, error } = useQuery({
    queryKey: ['pin-detail', url],
    queryFn: () =>
      api.get<(Ukol | Vada) & { prilohy: { id: string; nazev: string; popis?: string | null }[] }>(
        url,
      ),
  });

  const zmenStav = useMutation({
    mutationFn: (stav: string) => api.patch(url, { stav }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vykres-pins'] });
      void queryClient.invalidateQueries({ queryKey: ['pin-detail', url] });
    },
  });

  const ukol = pin.typ === 'ukol' ? (data as Ukol | undefined) : undefined;
  const vada = pin.typ === 'vada' ? (data as Vada | undefined) : undefined;
  const dalsi = ukol ? UKOL_DALSI_STAV[ukol.stav] : vada ? VADA_DALSI_STAV[vada.stav] : undefined;

  return (
    <BottomSheet nazev={pin.typ === 'ukol' ? 'Detail úkolu' : 'Detail vady'} zavri={zavri}>
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
      {!data && !error && <Nacitani />}
      {ukol && (
        <>
          <div className="flex items-start justify-between gap-3">
            <p className="text-lg font-bold">{ukol.nazev}</p>
            <Badge barva={UKOL_STAV_BARVA[ukol.stav]}>{UKOL_STAV_LABEL[ukol.stav]}</Badge>
          </div>
          {ukol.popis ? <p className="text-base whitespace-pre-wrap">{ukol.popis}</p> : null}
          {ukol.termin ? <p className="text-base">Termín: {formatDen(ukol.termin)}</p> : null}
          {(ukol.prirazenoUser || ukol.prirazenoSubdodavatel) && (
            <p className="text-base">
              Přiřazeno: {ukol.prirazenoUser?.jmeno ?? ukol.prirazenoSubdodavatel?.nazev}
            </p>
          )}
        </>
      )}
      {vada && (
        <>
          <div className="flex items-start justify-between gap-3">
            <p className="text-lg font-bold">{vada.popis}</p>
            <Badge barva={VADA_STAV_BARVA[vada.stav]}>{VADA_STAV_LABEL[vada.stav]}</Badge>
          </div>
          <p className="text-base text-text-secondary">
            fáze vzniku: {VADA_FAZE_LABEL[vada.fazeVzniku]}
          </p>
          {vada.termin ? <p className="text-base">Termín: {formatDen(vada.termin)}</p> : null}
          {vada.kdoOpravi ? <p className="text-base">Opraví: {vada.kdoOpravi.nazev}</p> : null}
        </>
      )}
      {data && data.prilohy.length > 0 && (
        <div className="flex gap-2 overflow-x-auto">
          {data.prilohy.map((p) => (
            <img
              key={p.id}
              src={`/api/diary/prilohy/${p.id}/soubor`}
              alt={p.popis ?? p.nazev}
              className="h-24 w-24 shrink-0 rounded-md border border-border object-cover"
              loading="lazy"
            />
          ))}
        </div>
      )}
      {smiZapisovat && dalsi && (
        <VelkeTlacitko disabled={zmenStav.isPending} onClick={() => zmenStav.mutate(dalsi)}>
          {pin.typ === 'ukol'
            ? dalsi === 'probiha'
              ? '▶ ZAČÍT'
              : '✓ HOTOVO'
            : dalsi === 'opraveno'
              ? '✓ OPRAVENO'
              : '✓ PŘEKONTROLOVÁNO'}
        </VelkeTlacitko>
      )}
      {zmenStav.error ? <Chyba>{hlaskaZChyby(zmenStav.error)}</Chyba> : null}
    </BottomSheet>
  );
}

/** Miniformulář nového pinu: volba Úkol/Vada, název/popis, u vady fáze, foto. */
function NovyPinSheet({
  zakazkaId,
  verzeId,
  bod,
  zavri,
}: {
  zakazkaId: string;
  verzeId: string;
  bod: { x: number; y: number };
  zavri: () => void;
}) {
  const queryClient = useQueryClient();
  const [typ, setTyp] = useState<'ukol' | 'vada'>('ukol');
  const [text, setText] = useState('');
  const [fazeVzniku, setFazeVzniku] = useState<string>('prubezna');
  const [foto, setFoto] = useState<File | null>(null);
  const [odesilam, setOdesilam] = useState(false);
  const [chyba, setChyba] = useState<string | null>(null);

  // Pin se ukládá na KONKRÉTNÍ zobrazenou verzi, souřadnice 0–1 (lib/pin.ts).
  const pinPole = useMemo(
    () => ({ drawingVersionId: verzeId, pinX: bod.x, pinY: bod.y }),
    [verzeId, bod],
  );

  const pripnout = async () => {
    setOdesilam(true);
    setChyba(null);
    try {
      const zalozeny =
        typ === 'ukol'
          ? await api.post<Ukol>('/api/tasks', { zakazkaId, nazev: text, ...pinPole })
          : await api.post<Vada>('/api/defects', {
              zakazkaId,
              popis: text,
              fazeVzniku,
              ...pinPole,
            });
      if (foto) {
        const form = new FormData();
        form.set('soubor', foto, foto.name);
        form.set('nazevSouboru', foto.name || 'pin.jpg');
        form.set('mimeType', foto.type || 'image/jpeg');
        await api.upload(
          `/api/${typ === 'ukol' ? 'tasks' : 'defects'}/${zalozeny.id}/prilohy`,
          form,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['vykres-pins', verzeId] });
      zavri();
    } catch (err) {
      setChyba(hlaskaZChyby(err));
    } finally {
      setOdesilam(false);
    }
  };

  return (
    <BottomSheet nazev="Nový pin" zavri={zavri}>
      {chyba ? <Chyba>{chyba}</Chyba> : null}
      <div className="grid grid-cols-2 gap-2" role="group" aria-label="Úkol, nebo vada">
        <button
          type="button"
          aria-pressed={typ === 'ukol'}
          className={`min-h-12 rounded-lg text-lg font-bold transition-colors duration-100 ${
            typ === 'ukol'
              ? 'border-2 border-info bg-info-bg text-info-text'
              : 'border-2 border-border-strong bg-surface'
          }`}
          onClick={() => setTyp('ukol')}
        >
          Úkol
        </button>
        <button
          type="button"
          aria-pressed={typ === 'vada'}
          className={`min-h-12 rounded-lg text-lg font-bold transition-colors duration-100 ${
            typ === 'vada'
              ? 'border-2 border-error bg-error-bg text-error-text'
              : 'border-2 border-border-strong bg-surface'
          }`}
          onClick={() => setTyp('vada')}
        >
          Vada
        </button>
      </div>
      {typ === 'ukol' ? (
        <Input
          aria-label="Název úkolu"
          placeholder="Co se má provést…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
      ) : (
        <>
          <Textarea
            aria-label="Popis vady"
            rows={2}
            placeholder="Co je špatně…"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <label className="block">
            <span className="mb-1 block text-base font-semibold">Fáze vzniku</span>
            <Select
              aria-label="Fáze vzniku"
              value={fazeVzniku}
              onChange={(e) => setFazeVzniku(e.target.value)}
            >
              {VADA_FAZE_VZNIKU.map((f) => (
                <option key={f} value={f}>
                  {VADA_FAZE_LABEL[f]}
                </option>
              ))}
            </Select>
          </label>
        </>
      )}
      <label className="flex min-h-12 cursor-pointer items-center justify-center rounded-lg border-2 border-border-strong bg-surface px-4 text-base font-bold transition-colors duration-100 hover:bg-bg">
        <Camera size={18} strokeWidth={2} aria-hidden="true" className="mr-2 inline" />
        {foto ? foto.name : 'Foto (volitelné)'}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          aria-label="Foto k pinu"
          onChange={(e) => setFoto(e.target.files?.[0] ?? null)}
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <VelkeTlacitko disabled={!text.trim() || odesilam} onClick={() => void pripnout()}>
          {odesilam ? 'Připínám…' : typ === 'ukol' ? 'Připnout úkol' : 'Připnout vadu'}
        </VelkeTlacitko>
        <VelkeTlacitko varianta="secondary" disabled={odesilam} onClick={zavri}>
          Zrušit
        </VelkeTlacitko>
      </div>
    </BottomSheet>
  );
}

/** Jednoduchý bottom sheet — plovoucí panel od spodní hrany (terén i kancelář). */
function BottomSheet({
  nazev,
  zavri,
  children,
}: {
  nazev: string;
  zavri: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-overlay">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={nazev}
        className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-t-lg bg-surface p-5 shadow-xl"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-xl font-bold">{nazev}</h2>
          <button
            type="button"
            className="min-h-12 min-w-12 rounded-md text-2xl leading-none text-text-secondary transition-colors duration-100 hover:bg-bg"
            aria-label="Zavřít"
            onClick={zavri}
          >
            ✕
          </button>
        </div>
        <div className="flex flex-col gap-3">{children}</div>
      </div>
    </div>
  );
}
